import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Badge, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const CATEGORIES = ['grammar','vocabulary','exercises','other'];
const CAT_LABELS  = { grammar:'Grammar', vocabulary:'Vocabulary', exercises:'Exercises', other:'Other' };
const CAT_ICONS   = { grammar:'📐', vocabulary:'📖', exercises:'✏️', other:'📎' };
const FILE_COLORS = {
  pdf:  'bg-brand-50 text-brand-600 border border-brand-200',
  docx: 'bg-blue-50 text-blue-600 border border-blue-200',
  doc:  'bg-blue-50 text-blue-600 border border-blue-200',
  pptx: 'bg-orange-50 text-orange-600 border border-orange-200',
  png:  'bg-green-50 text-green-600 border border-green-200',
  jpg:  'bg-green-50 text-green-600 border border-green-200',
};

export default function StudentMaterialsPage() {
  const { addToast } = useToast();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    API.get('/materials')
      .then(({ data }) => setMaterials(data.materials || []))
      .catch(() => addToast('Failed to load materials', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeTab === 'all'
    ? materials
    : materials.filter(m => m.category === activeTab);

  const grouped = filtered.reduce((acc, m) => {
    const key = m.level;
    if (!acc[key]) acc[key] = [];
    acc[key].push(m);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <SectionHeader
        title="Learning Materials"
        subtitle="Download study resources for your German level"
      />

      {/* Category tabs */}
      <div className="flex gap-1 p-1 bg-surface rounded-xl mb-6 overflow-x-auto">
        {['all', ...CATEGORIES].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize transition-all whitespace-nowrap
              ${activeTab === tab ? 'bg-white text-brand-500 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab === 'all' ? '📚 All' : `${CAT_ICONS[tab]} ${CAT_LABELS[tab]}`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" className="text-brand-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-4xl mb-3">📂</p>
          <p className="text-base font-bold text-de-black mb-1">No materials yet</p>
          <p className="text-sm text-gray-500">Your teacher hasn't uploaded any materials for this category.</p>
        </div>
      ) : (
        ['A1','A2','B1','B2'].map(level => {
          const levelMats = grouped[level];
          if (!levelMats?.length) return null;
          return (
            <div key={level} className="mb-8">
              <h2 className="text-sm font-bold text-de-black uppercase tracking-wide mb-3 flex items-center gap-2">
                <span className="px-2.5 py-1 bg-brand-500 text-white rounded-lg text-sm">{level}</span>
                <span className="text-gray-400 font-normal">{levelMats.length} item{levelMats.length !== 1 ? 's' : ''}</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {levelMats.map(mat => {
                  const ext = mat.file_type?.toLowerCase().replace(/^\./, '') || 'file';
                  const badgeClass = FILE_COLORS[ext] || 'bg-surface text-gray-600 border border-gray-200';
                  return (
                    <Card key={mat.id} className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded uppercase ${badgeClass}`}>
                              {ext}
                            </span>
                            <Badge variant="default" size="xs">{CAT_ICONS[mat.category]} {CAT_LABELS[mat.category]}</Badge>
                          </div>
                          <h3 className="text-sm font-bold text-de-black truncate">{mat.title}</h3>
                          {mat.description && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{mat.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2">
                            {mat.file_size && <span className="text-xs text-gray-400">{mat.file_size}</span>}
                            <span className="text-xs text-gray-400">
                              {new Date(mat.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}
                            </span>
                          </div>
                        </div>
                        <a href={mat.file_url} target="_blank" rel="noopener noreferrer"
                          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-brand-500 text-white rounded-xl text-xs font-bold hover:bg-brand-600 transition-colors">
                          ↓ Download
                        </a>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
